import React from 'react';
import { useTranslation } from 'react-i18next';
import BulletList from './BulletList';
import WorkoutAdaptationCompareCard from './WorkoutAdaptationCompareCard';

export default function CoachRecommendations({
  coachAnalysis,
  modifications,
  canEdit,
  modificationsApplied,
  modificationsDismissed,
  applyingMod,
  dismissingMod,
  onApply,
  onDismiss
}) {
  const { t } = useTranslation();
  const rec = coachAnalysis?.recommendations;
  if (!rec) return null;

  const hasMod = modifications && Object.keys(modifications).length > 0;
  const preview = Array.isArray(rec?.adjust_plan?.preview_workouts) ? rec.adjust_plan.preview_workouts : null;
  const sanitizeRecommendation = (s) => {
    if (typeof s !== 'string') return s;
    // Strip any accidental internal ids (defensive; prompt should avoid them).
    return s
      .replace(/\(id:\s*[0-9a-f-]{8,}\s*\)/gi, '')
      .replace(/\bid:\s*[0-9a-f-]{8,}\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
          {t('workout.recommendations')}
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-xs font-bold text-gray-600 uppercase tracking-wide">
              {t('workout.next_48h')}
            </div>
            <BulletList items={(rec.next_48h || []).map(sanitizeRecommendation)} className="mt-2" />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-600 uppercase tracking-wide">
              {t('workout.next_key_sessions')}
            </div>
            <BulletList items={(rec.next_key_sessions || []).map(sanitizeRecommendation)} className="mt-2" />
          </div>
        </div>
      </div>

      {hasMod ? (
        <div className="mt-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1.5 h-7 bg-yellow-500 rounded-full"></div>
                <h4 className="text-2xl font-black text-gray-900 tracking-tight">
                  {t('workout.suggested_adaptations')}
                </h4>
              </div>
              <p className="text-gray-500 text-sm">{t('workout.adaptations_intro')}</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {preview && preview.length > 0
              ? preview.map((w, index) => (
                  w?.prev && w?.suggested ? (
                    <div key={index} className="bg-yellow-50/50 rounded-2xl border border-yellow-100 p-4">
                        <WorkoutAdaptationCompareCard item={w} />
                    </div>
                  ) : (
                    <div key={index} className="bg-yellow-50/50 p-5 rounded-2xl border border-yellow-100 shadow-sm transition-all hover:border-yellow-200">
                      <div className="flex justify-between items-center mb-3 border-b border-yellow-100/50 pb-3">
                        <div className="min-w-0">
                          <div className="text-xs text-yellow-600/80 font-mono font-bold tracking-wide">
                            {w.date ? String(w.date) : (w.day ? String(w.day) : '')}
                          </div>
                          <div className="font-black text-gray-900 truncate text-base">{w.title || w.id}</div>
                        </div>
                        <div className="flex gap-2 text-xs font-black uppercase text-yellow-700/80">
                          {w.distance_km ? <span className="bg-yellow-100/50 px-2 py-1 rounded-md">{w.distance_km} km</span> : null}
                          {w.duration_min ? <span className="bg-yellow-100/50 px-2 py-1 rounded-md">{w.duration_min} min</span> : null}
                          {w.altitude_gain_m ? <span className="bg-yellow-100/50 px-2 py-1 rounded-md">+{w.altitude_gain_m} m</span> : null}
                        </div>
                      </div>
                      {w.description ? <p className="text-sm text-gray-700 leading-relaxed font-medium">{w.description}</p> : null}
                    </div>
                  )
                ))
              : Object.entries(modifications).map(([key, mod], index) => (
                  <div key={index} className="bg-yellow-50/50 p-5 rounded-2xl border border-yellow-100 shadow-sm transition-all hover:border-yellow-200">
                    <div className="flex justify-between items-center mb-3 border-b border-yellow-100/50 pb-3">
                      <span className="font-black text-gray-900 text-lg">{key}</span>
                      <div className="flex gap-2 text-xs font-black uppercase text-yellow-700/80">
                        {mod.distance_km ? <span className="bg-yellow-100/50 px-2 py-1 rounded-md">{mod.distance_km} km</span> : null}
                        {mod.duration_min ? <span className="bg-yellow-100/50 px-2 py-1 rounded-md">{mod.duration_min} min</span> : null}
                      </div>
                    </div>
                    {mod.title ? <div className="font-bold text-gray-900 mb-1">{mod.title}</div> : null}
                    {mod.description ? <p className="text-sm text-gray-700 leading-relaxed font-medium">{mod.description}</p> : null}
                  </div>
                ))}
          </div>

          {modificationsApplied ? (
            <div className="w-full py-4 rounded-xl font-bold bg-green-100 text-green-800 text-center border border-green-200 shadow-sm">
              <span>✅</span> {t('workout.recommendations_applied')}
            </div>
          ) : modificationsDismissed ? (
            <div className="w-full py-4 rounded-xl font-bold bg-gray-50 text-gray-600 text-center border border-gray-200">
              <span>✖️</span> {t('workout.recommendations_dismissed')}
            </div>
          ) : !canEdit ? (
            <div className="w-full py-4 rounded-xl font-bold bg-gray-50 text-gray-500 text-center border border-gray-200">
              {t('workout.read_only')}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <button
                onClick={onApply}
                disabled={applyingMod || dismissingMod}
                className={`w-full py-4 rounded-2xl font-black text-white transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 text-lg ${
                  applyingMod ? 'bg-yellow-400 cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-600 shadow-yellow-500/30'
                }`}
              >
                {applyingMod ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    {t('workout.applying')}
                  </>
                ) : (
                  <>
                    <span>✨</span> {t('workout.apply_recommendations')}
                  </>
                )}
              </button>

              <button
                onClick={onDismiss}
                disabled={applyingMod || dismissingMod}
                className="w-full py-4 rounded-2xl font-bold text-gray-400 hover:bg-gray-50 border-2 border-transparent hover:border-gray-100 transition-all"
              >
                {t('workout.dismiss_recommendations')}
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}


